import { PropertyValues, css, html, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { OxGristRenderer } from './ox-grist-renderer.js'

@customElement('ox-grist-tree-renderer')
export class OxGristRendererTree extends OxGristRenderer {
  static styles = css`
    :host {
      overflow: hidden;
    }

    div[wrap] {
      position: relative;

      display: flex;
      align-items: center;
      gap: 6px;

      width: 100%;
      padding-left: calc(var(--tree-depth, 0) * 18px);
    }

    span[expander] {
      display: inline-block;
      vertical-align: middle;
      width: 12px;
      height: 20px;
      cursor: pointer;
      position: relative;
    }

    span[expander][collapsed]::before {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-25%, -50%) rotate(-90deg);
      content: ' ';
      border: 5px solid transparent;
      border-top: 5px solid var(--md-sys-color-on-primary-container, #1890ff);
    }

    span[expander][expanded]::before {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -25%);
      content: ' ';
      border: 5px solid transparent;
      border-top: 5px solid var(--md-sys-color-on-primary-container, #1890ff);
    }

    span[checkbox] {
      display: inline-block;
      vertical-align: middle;
      width: 12px;
      height: 20px;
      cursor: pointer;
      position: relative;
    }

    span[checkbox]::before {
      cursor: pointer;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      content: ' ';
      display: block;
      width: 10px;
      height: 10px;
      border: 1px solid var(--md-sys-color-on-primary-container, #1890ff);
      border-radius: 2px;
    }

    span[label] {
      flex: 1;
    }

    span[checkbox][checked='checked']::before {
      background-color: var(--md-sys-color-on-primary-container, #1890ff);
      border-color: var(--md-sys-color-on-primary-container, #1890ff);
    }

    span[checkbox][checked='checked']::after {
      position: absolute;
      content: ' ';
      display: block;
      top: 50%;
      left: 50%;
      width: 3px;
      height: 7px;
      border: 2px solid #fff;
      border-top: none;
      border-left: none;
      -webkit-transform: translate(-50%, -50%) rotate(45deg);
      -ms-transform: translate(-50%, -50%) rotate(45deg);
      transform: translate(-50%, -50%) rotate(45deg);
    }

    span[checkbox][checked='half-checked']::before {
      background-color: var(--md-sys-color-on-primary-container, #1890ff);
      border-color: var(--md-sys-color-on-primary-container, #1890ff);
    }

    span[checkbox][checked='half-checked']::after {
      position: absolute;
      content: ' ';
      display: block;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 10px;
      height: 2px;
      background-color: var(--md-sys-color-surface);
    }
  `

  @state() private checked?: 'checked' | 'half-checked' | 'unchecked'
  @state() private expanded?: boolean = false

  get rendererTemplate() {
    var { selectable = false } = this.column.record.options || {}
    var { __children__ } = this.record

    const expandable = __children__ && __children__.length > 0

    return html`
      <div wrap>
        ${expandable
          ? html`
              <span
                expander
                @click=${this.onClickExpander.bind(this)}
                ?expanded=${this.expanded}
                ?collapsed=${!this.expanded}
              ></span>
            `
          : html`<span expander></span>`}
        ${selectable
          ? html` <span checkbox @click=${this.onClickCheckbox.bind(this)} checked=${ifDefined(this.checked)}></span>`
          : nothing}

        <span label>${this.value}</span>
      </div>
    `
  }

  updated(changes: PropertyValues<this>) {
    var { __depth__, __check_in_tree__, __expanded__ } = this.record
    this.checked = __check_in_tree__
    this.expanded = __expanded__

    this.style.setProperty('--tree-depth', String(__depth__))
  }

  onClickCheckbox(e: MouseEvent) {
    e.stopPropagation()

    this.dispatchEvent(
      new CustomEvent('check-in-tree', {
        bubbles: true,
        composed: true,
        detail: this.record
      })
    )

    this.requestUpdate()
  }

  onClickExpander(e: MouseEvent) {
    e.stopPropagation()

    this.dispatchEvent(
      new CustomEvent(this.record.__expanded__ ? 'collapse-node' : 'expand-node', {
        bubbles: true,
        composed: true,
        detail: this.record
      })
    )

    this.requestUpdate()
  }

  get editableOnClick() {
    return false
  }
}
